import * as React from "react"
import * as Yup from "yup"
import { isString } from "lodash"
import {
  Button,
  ThemeCss,
  InputFieldBlock,
  ButtonProps,
  useShowSuccessToast,
  StyledPanelBoilerplate,
  StyledPanelBodySection,
} from "gatsby-interface"
import { MdAdd, MdDeleteForever, MdContentCopy } from "react-icons/md"
import { envVars as text } from "@modules/locales/default.js"
import { visuallyHiddenCss } from "@modules/a11y/stylesheets"
import { EnvVarsBulkForm } from "./EnvVarsBulkForm"
import { EnvVarsUiSource } from "../types"
import { StyledForm } from "@modules/ui/components/Primitives"
import {
  collectEnvVarsErrors,
  validationSchema,
  useEnvVarsFormAnalytics,
  getInitialVariablesValue,
  stripEmptyState,
  FormValues,
  FormErrors,
  baseFieldSchema,
  EnvVarFieldValue,
  EnvVarsFieldName,
  envVarFieldsToMap,
} from "./EnvironmentVariablesForm.helpers"
import copyToClipboard from "@modules/toolkit/helpers/copyToClipboard"
import { useTracker } from "@modules/analytics"

const { useEffect, useState, useContext } = React

const buttonSetCss: ThemeCss = theme => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",

  [theme.mediaQueries.tablet]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
})

const addButtonCss: ThemeCss = theme => ({
  width: "100%",
  marginBottom: theme.space[5],

  [theme.mediaQueries.tablet]: {
    width: "auto",
    marginRight: theme.space[5],
    marginBottom: 0,
  },
})

export type EnvironmentVariablesFormProps = {
  onSubmit: (values: FormValues) => void
  onValidationError: (
    errorInfo: ReturnType<typeof collectEnvVarsErrors>
  ) => void
  children?: React.ReactNode
}

type EnvironmentVariablesFormBag = {
  formErrors: FormErrors
  setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  validateField: React.ChangeEventHandler<HTMLInputElement>
  buildVariablesFieldRef?: React.RefObject<EnvVarsFieldArrayAPI>
  previewVariablesFieldRef?: React.RefObject<EnvVarsFieldArrayAPI>
}

const EnvironmentVariablesFormContext = React.createContext<
  EnvironmentVariablesFormBag
>({
  formErrors: {},
  setFormErrors: () => undefined,
  validateField: () => undefined,
  buildVariablesFieldRef: undefined,
  previewVariablesFieldRef: undefined,
})

export function EnvironmentVariablesForm({
  onSubmit,
  onValidationError,
  children,
}: EnvironmentVariablesFormProps) {
  const [formErrors, setFormErrors] = React.useState<FormErrors>({})
  const buildVariablesFieldRef = React.useRef<EnvVarsFieldArrayAPI>(null)
  const previewVariablesFieldRef = React.useRef<EnvVarsFieldArrayAPI>(null)

  const validateField: React.ChangeEventHandler<HTMLInputElement> = e => {
    // Assign input name to a variable as we won't be able to access it in async callbacks
    const fieldName = e.currentTarget.name

    baseFieldSchema
      .validate(e.currentTarget.value)
      .then(() => {
        if (formErrors[fieldName]) {
          setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: undefined,
          }))
        }
      })
      .catch((error: Yup.ValidationError) => {
        setFormErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: error.message,
        }))
      })
  }

  return (
    <StyledForm
      onSubmit={e => {
        e.preventDefault()
        e.stopPropagation()

        /**
         * Users might want to only have env vars for builds OR previews, in which case they might not fill the initial
         * empty row in the other tab; so we need to omit these "empty state" rows from the validated and submitted values
         */
        const buildVariables = stripEmptyState(
          buildVariablesFieldRef?.current?.getValues() || []
        )
        const previewVariables = stripEmptyState(
          previewVariablesFieldRef?.current?.getValues() || []
        )

        const values: FormValues = {
          buildVariables,
          previewVariables,
        }

        setFormErrors({})

        try {
          validationSchema.validateSync(values, {
            abortEarly: false,
          })
        } catch (validationError) {
          const errorsInfo = collectEnvVarsErrors(
            validationError as Yup.ValidationError
          )

          setFormErrors(errorsInfo.errors)
          onValidationError(errorsInfo)
          return
        }

        onSubmit(values)
      }}
    >
      <EnvironmentVariablesFormContext.Provider
        value={{
          formErrors,
          setFormErrors,
          validateField,
          buildVariablesFieldRef,
          previewVariablesFieldRef,
        }}
      >
        {children}
      </EnvironmentVariablesFormContext.Provider>
    </StyledForm>
  )
}

const rowCss: ThemeCss = _theme => ({
  verticalAlign: `middle`,
  "& > td": {
    paddingRight: 0,
  },
  "&:last-of-type > td": {
    borderBottom: `none`,
  },
  label: visuallyHiddenCss,
})

type EnvVarsFieldArrayAPI = {
  getValues: () => EnvVarFieldValue[]
}

type EnvVarsFieldArrayProps = {
  siteId: string
  envVars: EnvVarFieldValue[]
  name: EnvVarsFieldName
  uiSource: EnvVarsUiSource
}


export function EnvVarsFieldArray({
  siteId,
  envVars,
  name,
  uiSource,
}: EnvVarsFieldArrayProps) {
  const cachedEnvVars = new Map<string, string>()
  const [showBulkAddModal, setShowBulkAddModal] = useState<boolean>(false)
  const tracker = useEnvVarsFormAnalytics(siteId, uiSource)
  const showSuccessToast = useShowSuccessToast()
  const [currentEnvVars, setCurrentEnvVars] = useState<EnvVarFieldValue[]>(
    getInitialVariablesValue(envVars)
  )
  const {
    formErrors,
    setFormErrors,
    validateField,
    buildVariablesFieldRef,
    previewVariablesFieldRef,
  } = useContext(EnvironmentVariablesFormContext)

  useEffect(() => {
    return () => cachedEnvVars.clear()
  }, [])

  useEffect(() => {
    /**
     * If props.envVars has differences between the current values and values that previoulsy came in on props,
     * then our database has been updated (probably by quick connect) and we need to reflect this in the env var
     * fields so the user can see those updated values
     */
    let shouldUpdateState = false
    const nextStateItems: EnvVarFieldValue[] = []

    envVars.forEach(envVar => {
      const { key, value } = envVar
      const prevValue = cachedEnvVars.get(key)

      const noPrevValue = !prevValue && !isString(prevValue)
      const valueUpdated = prevValue !== value

      if (noPrevValue || valueUpdated) {
        shouldUpdateState = true
        nextStateItems.push(envVar)
      }

      cachedEnvVars.set(key, value)
    })

    if (shouldUpdateState) {
      setCurrentEnvVars(prevState => {
        const fullMap = {
          ...envVarFieldsToMap(prevState),
          ...envVarFieldsToMap(nextStateItems),
        }

        const nextState = Object.entries(fullMap).reduce(
          (envVars, [key, value]) => {
            envVars.push({ key, value })
            return envVars
          },
          [] as EnvVarFieldValue[]
        )

        return nextState
      })
    }
  }, [envVars, setCurrentEnvVars])

  React.useImperativeHandle(
    name === `buildVariables`
      ? buildVariablesFieldRef
      : previewVariablesFieldRef,
    () => ({
      getValues: () => currentEnvVars,
    })
  )

  const { trackButtonClicked } = useTracker()

  const handleCopyClick = async () => {
    const envAsString = currentEnvVars
      .map(variable => `${variable.key}="${variable.value}"`)
      .join("\n")

    await copyToClipboard(envAsString)

    showSuccessToast(text.messages.copiedToClipBoard)
  }

  return (
    <React.Fragment>
      <table>
        <thead css={visuallyHiddenCss}>
          <tr>
            <th>{text.labels.keyFieldLabel}</th>
            <th>{text.labels.valueFieldLabel}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(currentEnvVars || []).map((envVar, idx) => {
            const keyFieldName = `${name}[${idx}].key`
            const valueFieldName = `${name}[${idx}].value`

            return (
              <tr key={idx} css={rowCss}>
                <td>
                  <InputFieldBlock
                    id={keyFieldName}
                    name={keyFieldName}
                    label={text.labels.keyFieldLabel}
                    placeholder={text.labels.keyFieldPlaceholder}
                    value={envVar.key}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      validateField(e)
                      const value = e.target.value
                      setCurrentEnvVars(prevEnvVars => {
                        const newEnvVars = [...prevEnvVars]
                        newEnvVars[idx].key = value
                        return newEnvVars
                      })
                    }}
                    error={formErrors[keyFieldName]}
                  />
                </td>
                <td>
                  <InputFieldBlock
                    id={valueFieldName}
                    name={valueFieldName}
                    label={text.labels.valueFieldLabel}
                    placeholder={text.labels.valueFieldPlaceholder}
                    value={envVar.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      validateField(e)
                      const value = e.target.value
                      setCurrentEnvVars(prevEnvVars => {
                        const newEnvVars = [...prevEnvVars]
                        newEnvVars[idx].value = value
                        return newEnvVars
                      })
                    }}
                    error={formErrors[valueFieldName]}
                  />
                </td>
                <td>
                  <Button
                    tone="DANGER"
                    variant="SECONDARY"
                    size="L"
                    rightIcon={<MdDeleteForever />}
                    aria-label={text.actions.removeVariableRow}
                    type="button"
                    onClick={() => {
                      tracker.trackRemoveButtonClick()
                      setCurrentEnvVars(prevEnvVars =>
                        prevEnvVars.filter((_, varIdx) => varIdx !== idx)
                      )
                    }}
                  >
                    {text.actions.removeVariableRowShort}
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div css={buttonSetCss}>
        <AddButton
          onClick={() => {
            tracker.trackAddButtonClick()
            setCurrentEnvVars(prevEnvVars => [
              ...prevEnvVars,
              { key: ``, value: `` },
            ])
          }}
        >
          {text.actions.addVariableRow}
        </AddButton>
        <AddButton
          onClick={() => {
            tracker.trackBulkAddButtonClick()
            setShowBulkAddModal(true)
          }}
        >
          {text.actions.addBulkVariables}
        </AddButton>

        <Button
          size="M"
          variant="SECONDARY"
          rightIcon={<MdContentCopy />}
          onClick={handleCopyClick}
        >
          {text.actions.copyAllVars}
        </Button>
      </div>
      {showBulkAddModal && (
        <StyledPanelBoilerplate
          isOpen={showBulkAddModal}
          aria-label={text.labels.bulkAddModal}
          header={text.headers.bulkAdd}
          onClose={() => setShowBulkAddModal(false)}
        >
          <StyledPanelBodySection>
            <EnvVarsBulkForm
              initialEnvVars={currentEnvVars}
              onSubmit={envVars => {
                trackButtonClicked(`Save environment variables`, {})
                const formattedEnvVars = Object.entries(envVars).map(
                  ([key, value]) => ({
                    key,
                    value,
                  })
                )

                // formattedEnvVars.forEach(kv => push(kv))
                setCurrentEnvVars(prevEnvVars => {
                  const newEnvVars: EnvVarFieldValue[] = []

                  // Preserve non-empty env vars that are also not going to be replaced with the bulk addition
                  for (const envVar of prevEnvVars) {
                    const isEmpty = envVar && !envVar.key && !envVar.value
                    const isUpdated = envVar && envVars[envVar.key]

                    if (isEmpty || isUpdated) {
                      continue
                    }
                    newEnvVars.push(envVar)
                  }

                  return [...newEnvVars, ...formattedEnvVars]
                })
                setFormErrors({})
                setShowBulkAddModal(false)
              }}
              onCancel={() => setShowBulkAddModal(false)}
            />
          </StyledPanelBodySection>
        </StyledPanelBoilerplate>
      )}
    </React.Fragment>
  )
}

type AddButtonProps = Pick<ButtonProps, "onClick" | "children">

function AddButton({ onClick, children }: AddButtonProps) {
  return (
    <Button
      variant="SECONDARY"
      size="M"
      css={addButtonCss}
      type="button"
      onClick={onClick}
      rightIcon={<MdAdd />}
    >
      {children}
    </Button>
  )
}
